import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/InputDefault';
import { Label } from '@/components/ui/label';
import { IconProvider } from '@/components/ui';
import { DialogSection, DialogField, DialogOption, ConditionOption, DialogVariable } from '@/lib/stores/dialogStore';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DialogFieldEditor } from './DialogFieldEditor';

interface DialogSectionEditorProps {
  section: DialogSection;
  sectionIndex: number;
  sectionIdentifiers: string[];
  sections?: { section_identifier: string; name: string }[];
  currentDialogId?: string;
  dialogTranslatable: boolean;
  speakerMode: 'none' | 'type' | 'free-text' | 'object';
  speakerTypeId?: string | null;
  speakerTemplateIds?: string[];
  dialogVariables?: DialogVariable[];
  isSelected?: boolean;
  onSectionClick?: () => void;
  onUpdateSection: (index: number, updates: Partial<DialogSection>) => void;
  onRemoveSection: (index: number) => void;
  onAddField: (sectionIndex: number) => void;
  onUpdateField: (sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => void;
  onRemoveField: (sectionIndex: number, fieldId: string) => void;
  onAddOption: (sectionIndex: number, fieldId: string) => void;
  onUpdateOption: (sectionIndex: number, fieldId: string, optionIndex: number, updates: Partial<DialogOption>) => void;
  onRemoveOption: (sectionIndex: number, fieldId: string, optionIndex: number) => void;
  onAddConditionOption: (sectionIndex: number, fieldId: string) => void;
  onUpdateConditionOption: (sectionIndex: number, fieldId: string, optionIndex: number, updates: Partial<ConditionOption>) => void;
  onRemoveConditionOption: (sectionIndex: number, fieldId: string, optionIndex: number) => void;
  gameId?: string;
  totalSections: number;
  t: any;
}

export const DialogSectionEditor = React.memo(function DialogSectionEditor({
  section,
  sectionIndex,
  sectionIdentifiers,
  sections,
  currentDialogId,
  dialogTranslatable,
  speakerMode,
  speakerTypeId,
  speakerTemplateIds,
  dialogVariables,
  isSelected = false,
  onSectionClick,
  onUpdateSection,
  onRemoveSection,
  onAddField,
  onUpdateField,
  onRemoveField,
  onAddOption,
  onUpdateOption,
  onRemoveOption,
  onAddConditionOption,
  onUpdateConditionOption,
  onRemoveConditionOption,
  gameId,
  totalSections,
  t,
}: DialogSectionEditorProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id || `section-fallback-${section.section_identifier}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Local state for section name
  const [nameValue, setNameValue] = useState(section.name || '');
  
  // State for collapse/expand
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Sync local state when section name changes externally
  useEffect(() => {
    setNameValue(section.name || '');
  }, [section.name]);

  // Sensors for field dragging
  const fieldSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle name change (update local state only)
  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNameValue(e.target.value);
  }, []);

  // Save name changes when focus is lost
  const handleNameBlur = useCallback(() => {
    if (nameValue !== section.name) {
      onUpdateSection(sectionIndex, { name: nameValue });
    }
  }, [nameValue, section.name, sectionIndex, onUpdateSection]);

  // Auto-generate section_identifier from name ONLY for new sections
  // CRITICAL FIX: Do NOT regenerate identifier when name changes - this breaks existing connections
  useEffect(() => {
    // Only generate identifier if section doesn't have one yet (new section)
    if (section.name && !section.section_identifier) {
      const identifier = section.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      
      if (identifier) {
        onUpdateSection(sectionIndex, { section_identifier: identifier });
      }
    }
  }, [section.name, section.section_identifier, sectionIndex, onUpdateSection]);

  // Handle field drag end within this section
  const handleFieldDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id || !section.fields) return;

    const activeIndex = section.fields.findIndex(f => f.id === active.id);
    const overIndex = section.fields.findIndex(f => f.id === over.id);

    if (activeIndex !== -1 && overIndex !== -1) {
      const reorderedFields = arrayMove(section.fields, activeIndex, overIndex);
      // Update order indices
      const updatedFields = reorderedFields.map((field, i) => ({
        ...field,
        order_index: i,
      }));
      onUpdateSection(sectionIndex, { fields: updatedFields });
    }
  }, [section.fields, sectionIndex, onUpdateSection]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onSectionClick?.()}
     
    >
      {/* Section Header with Inline Editable Title */}
      <div className="flex items-center gap-3 mb-3">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded text-muted-foreground"
          type="button"
        >
          <IconProvider iconstring="fa-grip-vertical" size={14} />
        </button>
        
        {/* Collapse/Expand Toggle */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-1 hover:bg-muted rounded text-muted-foreground transition-transform"
          type="button"
          title={isCollapsed ? "Expand section" : "Collapse section"}
        >
          <IconProvider 
            iconstring={isCollapsed ? "fa-chevron-right" : "fa-chevron-down"} 
            size={14} 
          />
        </button>
        
        <div className="flex-1">
          <Input
            value={nameValue}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            className="text-lg font-semibold border-0 bg-transparent p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            placeholder="Section title"
          />
        </div>
        
        {/* Field count badge when collapsed */}
        {isCollapsed && section.fields && section.fields.length > 0 && (
          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
            {section.fields.length} field{section.fields.length !== 1 ? 's' : ''}
          </span>
        )}
        
        {totalSections > 1 && (
          <Button
            onClick={() => onRemoveSection(sectionIndex)}
            size="sm"
            variant="ghost"
            className="text-xs w-9 h-9"
            type="button"
          >
            <IconProvider iconstring="fa-xmark" size={16} />
          </Button>
        )}
      </div>

      {/* Fields - Collapsible */}
      {!isCollapsed && (
        <div className="space-y-2">
          {section.fields && section.fields.length > 0 ? (
            <DndContext
              sensors={fieldSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleFieldDragEnd}
            >
              <SortableContext
                items={section.fields.map(f => f.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {section.fields.map((field, fieldIndex) => (
                    <DialogFieldEditor
                      key={field.id}
                      field={field}
                      fieldIndex={fieldIndex}
                      sectionIndex={sectionIndex}
                      sectionIdentifiers={sectionIdentifiers}
                      sections={sections}
                      currentSectionFields={section.fields}
                      currentDialogId={currentDialogId}
                      dialogTranslatable={dialogTranslatable}
                      speakerMode={speakerMode}
                      speakerTypeId={speakerTypeId}
                      speakerTemplateIds={speakerTemplateIds}
                      dialogVariables={dialogVariables}
                      onUpdateField={onUpdateField}
                      onRemoveField={onRemoveField}
                      onAddOption={onAddOption}
                      onUpdateOption={onUpdateOption}
                      onRemoveOption={onRemoveOption}
                      onAddConditionOption={onAddConditionOption}
                      onUpdateConditionOption={onUpdateConditionOption}
                      onRemoveConditionOption={onRemoveConditionOption}
                      gameId={gameId}
                      t={t}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : null}

          {/* Note: "Add Field" removed - sections now only contain one field (DialogNode concept) */}
        </div>
      )}
    </div>
  );
});

